import React, { SyntheticEvent, useEffect, useState } from 'react'
import * as yup from 'yup'
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Avatar,
  Grid,
  Card,
  Stack,
  TableCell,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  DialogTitle
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import api from 'services/api'
import useResponsive from 'hooks/useResponsive'
import { DataGridHeader } from './components/DataGridHeader'
import Iconify from 'components/Iconify'
import { deepOrange } from '@mui/material/colors'
import { getFirstLetter } from 'utils'
import { DataGrid } from '@mui/x-data-grid'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormInputText } from 'components/FormInputText'
import { Routine } from 'models/interfaces/routine.interface'
import { ProfessionalAthlete } from 'models/interfaces/professional-athlete.interface'

type ProfessionalAthleteRoutineTrainigFormProps = {
  id: string
  name: string
  note: string
}

type Props = {
  professionalAthleteId?: string
  isVisible: boolean
  athleteTraining: any
  onClose: () => void
}

export const CopyTrainingToModal = ({ professionalAthleteId, isVisible, athleteTraining, onClose }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState<boolean>(false)
  const [professionalAthleteResult, setProfessionalAthleteResult] = useState<ProfessionalAthlete[]>([])
  const [professionalAthleteToCopyResult, setProfessionalAthleteToCopyResult] = useState<ProfessionalAthlete[]>([])
  const mdUp = useResponsive('up', 'md')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [professionalAthleteRoutineList, setProfessionalAthleteRoutineList] = useState([])
  const [expanded, setExpanded] = useState<string | false>(false)

  const columns = [
    { field: 'name', headerName: t('routine.gridColumns.routine'), flex: 2 },
    { field: 'type', headerName: t('routine.gridColumns.type'), flex: 0.5 },
    { field: 'level', headerName: t('routine.gridColumns.level'), flex: 0.5 },
    { field: 'note', headerName: t('routine.gridColumns.note'), flex: 2 }
  ]

  const athleteRoutineSchema = yup.object({
    id: yup.string().required(() => t('general.validator.empty')).nullable(),
    name: yup.string().trim().required(() => t('general.validator.empty')),
    note: yup.string().trim().nullable()
  })

  const { control, handleSubmit, reset, setValue, formState: { errors } }: any = useForm<ProfessionalAthleteRoutineTrainigFormProps>({
    resolver: yupResolver(athleteRoutineSchema)
  })

  useEffect(() => {
    if (isVisible) {
      reset()
      setProfessionalAthleteRoutineList([])
      setValue('id', athleteTraining.id)
      dataFetch().catch(console.error)
    } else {
      setExpanded(false)
    }
  }, [isVisible])

  const dataFetch = async () => {
    try {
      setLoading(true)
      const { data } = await api.get('professional-athletes?active=true')
      const dataTranslation = data.map(
        (professionalAthlete: ProfessionalAthlete) => ({
          ...professionalAthlete,
          professionalAthleteRoutines: professionalAthlete?.professionalAthleteRoutines.map(
            (routine: Routine) => ({ ...routine, level: t(`routine.level.${routine?.level.toLowerCase()}`).toUpperCase(), type: t(`routine.types.${routine?.type.toLowerCase()}`).toUpperCase() })
          )
        })
      )
      await setProfessionalAthleteResult(dataTranslation)
      await setProfessionalAthleteToCopyResult(dataTranslation)
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const handleCopyTrainigToProfessionalAthleteRoutine = async (professionalAthleteRoutineTraining: ProfessionalAthleteRoutineTrainigFormProps) => {
    const professionalAthleteRoutineIds: any = []
    professionalAthleteRoutineList.forEach(function (data) {
      for (const key in data) {
        if (key === 'professionalAthleteRoutines') {
          Object.entries(data[key]).forEach(([id, value]) => { professionalAthleteRoutineIds.push(value) })
        }
      }
    })
    if (professionalAthleteRoutineIds.length < 1) {
      dispatch(showMessage({ message: `${t('routine.selectToCopy')}`, type: 'error' }))
      return
    }
    if (!professionalAthleteRoutineTraining) {
      dispatch(showMessage({ message: `${t('routine.forCopyNotFound')}`, type: 'error' }))
      return
    }
    try {
      await api.post(`/professional-athlete-trainings/${athleteTraining.id}/copy-professional-athlete-routine`, {
        name: professionalAthleteRoutineTraining.name,
        note: professionalAthleteRoutineTraining.note,
        professionalAthleteRoutineIds
      },
      { headers: { 'Content-Type': 'application/json' } })
      dispatch(showMessage({ message: `${t('training.copySuccess')}` }))
      onClose()
    } catch (e) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  const handleSearch = (e: any) => {
    if (!e.target.value) {
      setProfessionalAthleteToCopyResult(professionalAthleteResult)
      return
    }
    setProfessionalAthleteToCopyResult([])
    const dataFound: ProfessionalAthlete[] = []
    const searchText = e.target.value
    const regex = new RegExp(searchText.normalize('NFD').replace(/[\u0300-\u036f]/g, ''), 'i')
    professionalAthleteResult.forEach((obj) => {
      let firstName = ''
      let surName = ''
      Object.entries(obj.athlete).forEach(([key, value]) => {
        const textValue = value
        if (key === 'name') { firstName = textValue }
        if (key === 'surname') { surName = textValue }
        if (!dataFound.includes(obj) &&
          (key === 'email' || key === 'name' || key === 'surname') &&
          regex.exec(textValue.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
          dataFound.push(obj)
        }
      })
      if (!dataFound.includes(obj) &&
        regex.exec((`${firstName} ${surName}`).normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
        dataFound.push(obj)
      }
    })
    setProfessionalAthleteToCopyResult(dataFound)
  }

  return (
    <Box>
      <Dialog
        fullWidth={true}
        open={isVisible}
        maxWidth="lg"
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
      >
        <DialogTitle>{`${t('training.title.copyToRoutine')}`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={5}>
              <Controller
                name="name"
                control={control}
                defaultValue={athleteTraining?.name}
                render={({ field: { onChange, value } }) => (
                  <FormInputText
                    name="name"
                    value={value}
                    control={control}
                    placeholder={`${t('training.field.name.placeholder')}`}
                    label={`${t('training.field.name.label')}`}
                    onChange={onChange}
                    errorMessage={errors.name?.message}
                  />
                )} />
            </Grid>
            <Grid item xs={12} md={7}>
              <Controller
                name="note"
                control={control}
                defaultValue={athleteTraining?.note}
                render={({ field: { onChange, value } }) => (
                  <FormInputText
                    name="note"
                    value={value}
                    control={control}
                    placeholder={`${t('training.field.note.placeholder')}`}
                    label={`${t('training.field.note.label')}`}
                    onChange={onChange}
                  />
                )} />
            </Grid>
            <Grid item xs={12} >
              <Card variant="outlined">
                <Stack spacing={2}>
                  <TableContainer>
                    <Table aria-label="collapsible table">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <DataGridHeader
                              onActionSearch={(value) => handleSearch(value)}
                              onSearchTitle={`${t('search.titleAthlete')}`}
                            />
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { professionalAthleteToCopyResult?.sort((a, b) => a.athlete.name.localeCompare(b.athlete.name)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((professionalAthlete, key) => (
                          <TableRow key={`row_${key}`}>
                            <TableCell key={`cell_${key}`} sx={{ padding: 0.5 }}>
                              <Accordion
                                sx={{ border: 0, background: '#27272a' }}
                                expanded={expanded === `panel_${key}`}
                                onChange={handleChange(`panel_${key}`)}
                              >
                                <AccordionSummary
                                  expandIcon={<Iconify icon="material-symbols:arrow-drop-down"/>}
                                  aria-controls={`panel_${key}-content`}
                                  id={`panel_${key}-header`}
                                >
                                  <Container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: 0 }}>
                                    {professionalAthlete.athlete.imagePath
                                      ? <Avatar src={professionalAthlete.athlete?.imagePath} sx={{ width: 56, height: 56 }} />
                                      : <Avatar sx={{ bgcolor: 'rgb(250, 89, 62, 0.1)', width: 56, height: 56, fontWeight: 'bold', zIndex: 1 }}>
                                        <Typography component="h1" variant="h5" sx={{ color: deepOrange[500], zIndex: 9 }}>
                                          {getFirstLetter(professionalAthlete.athlete?.name)}
                                        </Typography>
                                      </Avatar>}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', paddingLeft: 1, textAlign: 'left' }}>
                                      <Typography variant="h6">{professionalAthlete.athlete?.name} {professionalAthlete.athlete?.surname}</Typography>
                                      <Typography variant="subtitle2">{professionalAthlete.athlete?.email}</Typography>
                                    </Box>
                                  </Container>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box sx={{ height: 240 }}>
                                    <DataGrid
                                      rows={professionalAthlete.professionalAthleteRoutines}
                                      initialState={{
                                        sorting: {
                                          sortModel: [{ field: 'name', sort: 'asc' }]
                                        }
                                      }}
                                      hideFooterPagination={true}
                                      checkboxSelection
                                      columns={mdUp ? columns : columns.filter((column) => (column.field === 'name'))}
                                      disableSelectionOnClick={true}
                                      disableColumnMenu={true}
                                      onSelectionModelChange={(ids) => {
                                        const routineSelected: any = []
                                        if (professionalAthleteRoutineList.length > 0) {
                                          professionalAthleteRoutineList.forEach((dataAthlete) => {
                                            routineSelected.push(dataAthlete)
                                          })
                                          const index = routineSelected.findIndex((val: { professionalAthleteId: string }) => val.professionalAthleteId === professionalAthlete.id)
                                          if (index < 0) {
                                            routineSelected.push({ professionalAthleteId: professionalAthlete.id, professionalAthleteRoutines: ids })
                                          } else {
                                            routineSelected.splice(index, 1)
                                            if (ids.length > 0) {
                                              routineSelected.push({ professionalAthleteId: professionalAthlete.id, professionalAthleteRoutines: ids })
                                            }
                                          }
                                          setProfessionalAthleteRoutineList(routineSelected)
                                          return
                                        }
                                        routineSelected.push({ professionalAthleteId: professionalAthlete.id, professionalAthleteRoutines: ids })
                                        setProfessionalAthleteRoutineList(routineSelected)
                                      }}
                                      sx={{
                                        '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                                          display: 'none'
                                        }
                                      }}
                                    />
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[20, 40, 80]}
                    component='div'
                    count={professionalAthleteToCopyResult.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={mdUp ? 'Rows per page' : ''}
                  />
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          { professionalAthleteResult &&
            <LoadingButton loading={loading} variant="contained" onClick={handleSubmit(handleCopyTrainigToProfessionalAthleteRoutine)}>
              {t('general.btn.save')}
            </LoadingButton>
          }
          <Button variant="contained" color="secondary" onClick={onClose}>
            {t('general.btn.dismiss')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
